import { ChangeEvent, useEffect, useState } from "react";
import { updateGeneral } from "@/dispatchers/generalDispatchers";
import { useTitanApp } from "@/hooks/useTitanApp";
import { getTeamsRoles } from "@/dispatchers/teamDispatchers";
import useTitanTeam from "@/hooks/useTitanTeam";
import {
  addRole,
  updateRole,
  deleteRoleById,
} from "@/dispatchers/teamDispatchers";
export interface Role {
  id: string;
  name: string;
  description: string;
  companyID?: string;
  isAdmin?: boolean;
  roleType?: number;
  permissions: Permissions[];
  isSytemPreDefined?: boolean | string | number;
  isSystemPreDefined?: boolean | string | number;
  systemRoleId?: string;
}
interface Permissions {
  id: string;
  label?: string;
  description?: string;
  friendlyName?: string;
}

export const defaultRoleSettings: Role = {
  id: "",
  name: "",
  description: "",
  companyID: "",
  isAdmin: false,
  roleType: 1,
  permissions: [],
};

export const permissionData: Permissions = {
  id: "",
  label: "",
  description: "",
};

const isSystemDefinedRole = (role: any) =>
  role?.isSytemPreDefined === true ||
  role?.isSytemPreDefined === "true" ||
  role?.isSytemPreDefined === 1 ||
  role?.isSystemPreDefined === true ||
  role?.isSystemPreDefined === "true" ||
  role?.isSystemPreDefined === 1;

export const useRoleSettingsData = () => {
  const { appState, onboardingVerificationState } = useTitanApp();
  const { teamState } = useTitanTeam();
  const { userData } = onboardingVerificationState || {};
  const { isTeamLoading, teamRolesAll, teamPermissions } = teamState || {};
  const { darkMode, companyId } = appState || {};
  const [selectedMenuItem, setSelectedMenuItem] = useState("Role");
  const isDarkMode = darkMode ?? false;
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissionModules, setPermissions] = useState<Permissions[]>([]);
  useEffect(() => {
    if (teamRolesAll && Array.isArray(teamRolesAll)) {
      const mappedRoles = teamRolesAll.map((role: any) => {
        // Only include permissions that the role actually has
        const rolePermsWithDetails = (role.permissions || []).map(
          (rolePerm: any) => {
            const permDetails = teamPermissions?.find(
              (perm: any) =>
                (perm._id?.$oid ?? perm._id) ===
                (rolePerm._id?.$oid ?? rolePerm._id),
            );

            return {
              id: permDetails?._id ?? rolePerm._id ?? "",
              label: permDetails?.permissionKey ?? rolePerm.permissionName,
              description:
                permDetails?.permissionDescription ??
                rolePerm.permissionDescription,
              friendlyName:
                permDetails?.permissionName ?? rolePerm.permissionName,
              isPermissionEnabled: rolePerm?.IsPermissionEnabled ?? true,
            };
          },
        );

        return {
          id: role._id ?? "",
          name: role.roleName ?? "",
          description: role.roleDescription ?? "",
          companyID: role.companyID ?? "",
          isAdmin: role.isAdmin ?? false,
          roleType: role.roleType ?? 1,
          permissions: rolePermsWithDetails,
          isSytemPreDefined: isSystemDefinedRole(role),
          isSystemPreDefined: isSystemDefinedRole(role),
          systemRoleId: role.systemRoleId ?? "",
        };
      });

      setRoles(mappedRoles);
    }

    // Keep master permissions list for reference
    if (teamPermissions && Array.isArray(teamPermissions)) {
      setPermissions(
        teamPermissions.map((permission: any) => ({
          id: permission._id ?? "",
          label: permission.permissionKey ?? "",
          description: permission.permissionDescription ?? "",
          friendlyName: permission.permissionName ?? "",
        })),
      );
    }
  }, [teamRolesAll, teamPermissions]);

  const [settings, setSettings] = useState<Role>({
    id: teamRolesAll._id,
    name: teamRolesAll.roleName,
    description: "",
    companyID: teamRolesAll.companyID,
    isAdmin: teamRolesAll.isAdmin,
    roleType: teamRolesAll.roleType,
    permissions: teamRolesAll.permissions,
  });

  const handleInputChange =
    (field: keyof Role) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSettings((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

  const saveRoleSettings = async (data) => {
    const roleData = {
      roleName: data.name || "",
      roleDescription: data.description || "",
      companyID: companyId,
      isAdmin: false,
      roleType: 1,
      systemRoleId: data.systemRoleId || "",
      permissions:
        data.permissions
          .filter((permission: any) => permission.isPermissionEnabled) // <-- filter only enabled
          .map((permission: any) => ({
            _id: permission?.id ?? "",
            permissionName: permission?.label ?? "",
            permissionDescription: permission?.description ?? "",
            isPermissionEnabled: true, // optional, keep consistent
          })) ?? [],
    };

    try {
      const response = await addRole(roleData);
      await getTeamsRoles(companyId);

      if (!response?.success) {
        // showError("Could not update general settings.", true, false);
      }
    } catch (error) {
      // showError("Update failed.", true, false);
    }
  };

  const updateRoleSettings = async (data) => {
    const roleData = {
      _id: data.id,
      roleName: data.name || "",
      roleDescription: data.description || "",
      companyID: companyId,
      isAdmin: false,
      roleType: 1,
      systemRoleId: data.systemRoleId || "",
      permissions:
        data.permissions
          .filter((permission: any) => permission.isPermissionEnabled)
          .map((permission: any) => ({
            _id: permission?.id ?? "",
            permissionName: permission?.label ?? "",
            permissionDescription: permission?.description ?? "",
            isPermissionEnabled: true,
          })) ?? [],
    };
    try {
      const response = await updateRole(roleData);
      if (!response?.success) {
        await getTeamsRoles(companyId);
      }
    } catch (error) {}
  };

  const deleteRoleSettings = async (roleId: string) => {
    try {
      const data = {
        RoleId: roleId,
        CompanyID: companyId,
      };

      const response = await deleteRoleById(data);
      await getTeamsRoles(companyId);

      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  };
  const overriddenSystemRoleIds = new Set(
    roles
      .map((role) => role.systemRoleId)
      .filter((systemRoleId): systemRoleId is string => !!systemRoleId),
  );

  const filteredRoles = roles.filter((role) => {
    const normalizedRoleName = role.name?.toLowerCase();
    const isSuperAdmin =
      normalizedRoleName === "super admin" ||
      normalizedRoleName === "superadmin";

    if (isSuperAdmin) {
      return false;
    }

    if (isSystemDefinedRole(role) && overriddenSystemRoleIds.has(role.id)) {
      return false;
    }

    return true;
  });

  return {
    isDarkMode,
    settings,
    teamPermissions,
    roles: filteredRoles,
    permissionModules,
    selectedMenuItem,
    isTeamLoading,
    setSelectedMenuItem,
    handleInputChange,
    saveRoleSettings,
    updateRoleSettings,
    deleteRoleSettings,
    userData,
  };
};

export const initialRoles = [];
